import PaletteModel from "ui/canvas/models/PaletteModel";
import CanvasLayer, {EVENT_VISIBILITY_CHANGED, EVENT_FRAME_LOADED} from "ui/canvas/CanvasLayer";
import EventDispatcher from "ui/EventDispatcher";
import DataSet from "ui/DataSet";
import CanvasFloodFill from "ui/canvas/CanvasFloodFill";
import CanvasClipArea from "ui/canvas/CanvasClipArea";
import Color from "ui/helpers/Color";
import ColorsSetModel, {EVENT_CHANGE_COLOR} from "ui/canvas/models/ColorsSetModel";
import Template from "ui/Template";
import CanvasTranslate from "ui/canvas/CanvasTranslate";
import CanvasDraw from "ui/canvas/CanvasDraw";
import CanvasSelection from "ui/canvas/CanvasSelection";
import Utils from "ui/helpers/Utils";
import SceneNeighbors, {EVENT_CHANGED as EVENT_NEIGHBOR_CHANGED} from "ui/scene/SceneNeighbors";
import CanvasEllipse from "ui/canvas/CanvasEllipse";
import {IS_HIGH_DPR, SCENE_GRID_INDENT} from "ui/config";
import CanvasEraser from "ui/canvas/transforms/CanvasEraser";
import cursorView from './html/cursor.handlebars';
import {CanvasBlendingMode, CanvasBlendingModeToCompositeOperation} from "ui/canvas/CanvasBlendingMode";
import CanvasContextMenu from "ui/canvas/CanvasContextMenu";

export const EVENT_UPDATE_TIMELINE = 'update_timeline';
export const EVENT_HISTORY_CHANGED = 'history_changed';
export const EVENT_SAVED = 'saved';
export const EVENT_SET_COLOR = 'set_color';
export const EVENT_SET_BRUSH = 'set_brush';
export const EVENT_CHANGE_TOOL = 'change_tool';
export const EVENT_PROCESS_START = 'process_start';
export const EVENT_PROCESS_FINISH = 'process_finish';
export const EVENT_DRAWING = 'drawing';

/**
 * Scale Factors
 *
 * @type {Array<number>}
 */
export const LIMIT_SCALE_FACTORS = [0.5, 1, 2, 3, 4, 5, 6, 7, 8];

/**
 * Min Brush sizes for tools
 *
 * @type {Object}
 */
export const LIMIT_BRUSH_MIN_SIZES = {
	'brush': 1,
	'brush_bg': 1,
	'ellipse': 1,
	'erase': 1
};

/**
 * Max Brush sizes for tools
 *
 * @type {Object}
 */
export const LIMIT_BRUSH_MAX_SIZES = {
	'brush': 30,
	'brush_bg': 30,
	'ellipse': 30,
	'erase': 30
};

/**
 * Max records in actions history (how many records can be undo)
 *
 * @type {number}
 */
export const LIMIT_MAX_HISTORY_RECORDS = 30;

/**
 * Display mode: no white background, no grid, show layers and neighbor scenes
 *
 * @type {number}
 */
export const DISPLAY_MODE_VIEW = 1;

/**
 * Display mode: white background only, no neighbor scenes and grid
 *
 * @type {number}
 */
export const DISPLAY_MODE_EDIT = 2;

/**
 * Canvas Class
 * The element for draw and edit image, separated on layers
 */
export default class Canvas {
	/**
	 * Class Constructor
	 *
	 * @param {Object} [options]
	 */
	constructor(options = {}) {
		/**
		 * @type {T_CanvasProps}
		 * @private
		 */
		this._canvasProps = {
			name: '',
			desc: '',
			author: '',
			email: ''
		};

		/**
		 * @type {EventDispatcher}
		 * @private
		 */
		this._events = new EventDispatcher();

		/**
		 * @type {DataSet}
		 * @private
		 */
		this._dataSet = new DataSet();

		/**
		 * List of the objects that have been removed, but they still exists in history
		 * and potentially may be restored
		 *
		 * @type {{layers: Map<string, CanvasLayer>, gcSequence: number}}
		 * @private
		 */
		this._trash = {
			layers: new Map(),
			gcSequence: 0,
		};

		/**
		 * @type {{
		 * current: number, frames: number, isShadowFrame: boolean, isSelectToEnd: boolean,
		 * shadowImg: HTMLImageElement,
		 * shadowFrame: ?number, fromFrame: number, toFrame: number
		 * }}
		 * @private
		 */
		this._timeline = {
			current: 0,
			frames: options.frames || 1,
			fromFrame: -1,
			toFrame: -1,
			isShadowFrame: false,
			isSelectToEnd: false,
			shadowFrame: null,
			shadowImg: null,
		};

		/**
		 * @type {{size: number, sizeByTools: Map<string,number>, displayHalfSize: number}}
		 * @private
		 */
		this._brush = {
			size: 1,
			sizeByTools: new Map([['erase', 5]]),
			displayHalfSize: 1
		};

		/**
		 * @type {PaletteModel}
		 * @private
		 */
		this._palette = options.palette || new PaletteModel();

		/**
		 * @type {ColorsSetModel}
		 * @private
		 */
		this._colorsSet = options.colorsSet || new ColorsSetModel();

		/**
		 * @type {number}
		 * @private
		 */
		this._width = +options.width || 1024;

		/**
		 * @type {number}
		 * @private
		 */
		this._height = +options.height || 1024;

		/**
		 * @type {number}
		 * @private
		 */
		this._sceneWidth = +options.sceneWidth || 1024;

		/**
		 * @type {number}
		 * @private
		 */
		this._sceneHeight = +options.sceneHeight || 1024;

		/**
		 * @type {CanvasClipArea}
		 * @private
		 */
		this._clipArea = new CanvasClipArea(this._sceneWidth, this._sceneHeight);

		/**
		 * @type {CanvasTranslate}
		 * @private
		 */
		this._translate = null;

		this._dprFactor = IS_HIGH_DPR ? 2 : 1;

		/**
		 * The canvas which user see. Size is equal to the viewport
		 *
		 * @type {T_CanvasElement}
		 * @private
		 */
		this._displayCanvas = Utils.createCanvas('canvas', this._width * this._dprFactor, this._height * this._dprFactor);
		this._displayCanvas.el.style.width = `${this._width}px`;
		this._displayCanvas.el.style.height = `${this._height}px`;

		/**
		 * Main canvas which contains final image for rendering in original size
		 *
		 * @type {T_CanvasElement}
		 * @private
		 */
		this._workCanvas = Utils.createCanvas('', this._sceneWidth, this._sceneHeight);

		/**
		 * @type {T_CanvasElement}
		 * @private
		 */
		this._layerCanvas = Utils.createCanvas('', this._sceneWidth, this._sceneHeight);

		/**
		 * @type {T_CanvasElement}
		 * @private
		 */
		this._layersUnder = Utils.createCanvas('', this._sceneWidth, this._sceneHeight);

		/**
		 * @type {(T_CanvasElement|CanvasLayer)[]}
		 * @private
		 */
		this._layersAboveRenderList = [];

		/**
		 * @type {T_CanvasElement[]}
		 * @private
		 */
		this._layersAboveCanvasesPool = [];

		/**
		 * Canvas for drawing lines and other stuff before this will be a part of layer image
		 *
		 * @type {T_CanvasElement}
		 * @private
		 */
		this._tempCanvas = Utils.createCanvas('', this._sceneWidth, this._sceneHeight);

		/**
		 * @type {CanvasDraw}
		 * @private
		 */
		this._draw = new CanvasDraw(this._clipArea);

		/**
		 * @type {CanvasEllipse}
		 * @private
		 */
		this._ellipseDrawer = null;

		/**
		 * @type {CanvasSelection}
		 * @private
		 */
		this._sel = new CanvasSelection(this, this._layerCanvas);

		/**
		 * Canvas for buffering some parts of image while drawing
		 *
		 * @type {T_CanvasElement}
		 * @private
		 */
		this._tempBufferCanvas = Utils.createCanvas('', this._sceneWidth, this._sceneHeight);

		this._handlers = {
			contextMenu: this._onContextMenu.bind(this),
			mouseDown: this._onMouseDown.bind(this),
			mouseMove: this._onMouseMove.bind(this),
			mouseMoveShadow: this._onMouseMoveShadow.bind(this),
			mouseUp: this._onMouseUp.bind(this),
			touchDown: this._onTouchDown.bind(this),
			touchMove: this._onTouchMove.bind(this),
			touchUp: this._onTouchUp.bind(this),
			mouseWheel: this._onMouseWheel.bind(this),
			layerImageUpdatedLazy: this._onLayerImageUpdatedLazy.bind(this),
			layerImageUpdated: this._onLayerImageUpdated.bind(this),
			layerStateChanged: this._onLayerStateChanged.bind(this),
			renderDisplayCanvas: () => {
				this._render.flag = true;
				this._renderDisplayCanvas();
				this._render.flag = false;
			},
			keyDown: this._onKeyDown.bind(this),
			keyUp: this._onKeyUp.bind(this),
			cancelAllPressedKeys: this._cancelAllPressedKeys.bind(this),
		};

		/**
		 * @private
		 */
		this._cursor = {
			x: 0,
			y: 0,
			elX: 0,
			elY: 0,
			srcX: 0,
			srcY: 0,
			startX: 0,
			startY: 0,
			lastClientX: null,
			lastClientY: null,
			isShift: false,
			isCtrl: false,
			isAlt: false,
			isDown: false,
			type: '',
			offsetX: 0,
			offsetY: 0,
			onDown: null,
			onMove: null,
			onUp: null,
			icon: null,
			cssClass: '',
			offsetTop: 0,
			el: null,
			circleEl: null,
			circleBackEl: null,
			textEl: null,
			textBackEl: null,
			isTraceNextPoint: false,
		};

		/**
		 * @type {string}
		 * @private
		 */
		this._tool = '';

		/**
		 * @type {{brushSize: number, tool: string, fill: Color, angle: number}}
		 * @private
		 */
		this._currentDrawTool = {
			tool: '',
			brushSize: 0,
			angle: 0,
			fill: null,
		};

		/**
		 * @type {{
		 *  factor: number, centerX: number, centerY: number, prevCenterX: number, prevCenterY: number,
		 *  srcX: number, srcY: number, srcWidth: number, srcHeight: number,
		 *  dstX: number, dstY: number, dstWidth: number, dstHeight: number,
		 *  srcToDistWidth: number, srcToDistHeight: number
		 *  }}
		 * @private
		 */
		this._scale = {
			factor: 1,
			centerX: this._sceneWidth / 2,
			centerY: this._sceneHeight / 2,
			prevCenterX: this._sceneWidth / 2,
			prevCenterY: this._sceneHeight / 2,
			srcX: 0,
			srcY: 0,
			srcWidth: 0,
			srcHeight: 0,

			dstX: 0,
			dstY: 0,
			dstWidth: 0,
			dstHeight: 0,

			srcToDistWidth: 0,
			srcToDistHeight: 0,
		};

		/**
		 * @type {Color}
		 * @private
		 */
		this._fill = new Color();

		/**
		 * @type {{
		 *  ignore: boolean, flag: boolean, requested: boolean, requestAnimationFrame: Function,
		 *  grid: boolean
		 * }}
		 * @private
		 */
		this._render = {
			ignore: true,
			flag: true,
			grid: true,
			requested: true,
			requestAnimationFrame: window.requestAnimationFrame
				|| window.mozRequestAnimationFrame
				|| window.webkitRequestAnimationFrame
				|| window.oRequestAnimationFrame
		};

		/**
		 *
		 * @type {{
		 *  current: (null|CanvasLayer),
		 *  list: Array<CanvasLayer>,
		 *  lastImagesHash: string,
		 * }}
		 * @private
		 */
		this._layers = {
			current: null,
			list: [],
			lastImagesHash: '',
		};

		/**
		 * @type {{index: number, records: Array.<T_State>}}
		 * @private
		 */
		this._history = {
			index: 0,
			records: []
		};

		/**
		 * @type {number}
		 * @private
		 */
		this._displayMode = DISPLAY_MODE_EDIT;

		/**
		 * @type {SceneNeighbors}
		 * @private
		 */
		this._neighborScenes = new SceneNeighbors();

		/**
		 * @type {string}
		 * @private
		 */
		this._blankImageData = this._layerCanvas.el.toDataURL('image/png');

		/**
		 * @type {number}
		 * @private
		 */
		this._loadImagesTimerId = 0;

		/**
		 * @type {?Array<null|T_ImageData>}
		 * @private
		 */
		this._clipboardBuffer = null;

		this._cursor.el = new Template(cursorView)
			.createElement({});

		this._cursor.circleBackEl = this._cursor.el.querySelector('circle.canvas-cursor__back');
		this._cursor.circleEl = this._cursor.el.querySelector('circle:not(.canvas-cursor__back)');
		this._cursor.textBackEl = this._cursor.el.querySelector('.canvas-cursor__text text.canvas-cursor__back');
		this._cursor.textEl = this._cursor.el.querySelector('.canvas-cursor__text text:not(.canvas-cursor__back)');

		this._createLayer();
		this.setBrush(1);
		this.setTool('brush');
		this.setColor(this._colorsSet.getCurrentColor());
		this.setScale(1);
		this.saveCurrentLayerImage();
		this._saveState('init');

		if (options.parent instanceof HTMLElement) {
			options.parent.appendChild(this._displayCanvas.el);
			options.parent.appendChild(this._cursor.el);
			this._listenEvents();
		}

		this._render.ignore = false;
		this._render.requested = true;
		this._render.flag = true;
		this._scale.centerY = (this._scale.centerY + 100) / this._scale.factor;
		this._cursor.offsetTop = this._displayCanvas.el.getBoundingClientRect().top;
		this._calcScale();
		this._updateWorkCanvas();
		this._renderDisplayCanvas();
		this.updateCursor();
	}

	/**
	 * Returns instance of the events dispatcher
	 *
	 * @returns {EventDispatcher}
	 */
	get events() {
		return this._events;
	}

	/**
	 * @returns {HTMLCanvasElement}
	 */
	get displayCanvas() {
		return this._displayCanvas.el;
	}

	/**
	 * Returns instance Colors Model
	 *
	 * @returns {ColorsSetModel}
	 */
	get colorsSet() {
		return this._colorsSet;
	}

	/**
	 * Returns instance of the selection
	 *
	 * @returns {CanvasSelection}
	 */
	get selection() {
		return this._sel;
	}

	/**
	 * Returns instance of the DatSet object, which stores large strings
	 *
	 * @returns {DataSet}
	 */
	get dataSet() {
		return this._dataSet;
	}

	/**
	 * Returns images data of the blank canvas
	 *
	 * @returns {string}
	 */
	get blankImageData() {
		return this._blankImageData;
	}

	/**
	 * Returns width of the scene
	 *
	 * @returns {number}
	 */
	get sceneWidth() {
		return this._sceneWidth;
	}

	/**
	 * Returns height of the scene
	 *
	 * @returns {number}
	 */
	get sceneHeight() {
		return this._sceneHeight;
	}

	/**
	 * Returns instance of the SceneNeighbors
	 *
	 * @returns {SceneNeighbors}
	 */
	get neighborScenes() {
		return this._neighborScenes;
	}

	/**
	 * @return {string}
	 */
	getCurrentFrameImageData() {
		let prevDisplayMode = this._displayMode;
		this._displayMode = DISPLAY_MODE_VIEW;
		this._updateWorkCanvas();
		const data = this._workCanvas.el.toDataURL('image/png');
		this._displayMode = prevDisplayMode;
		this._updateWorkCanvas();
		return data;
	}

	/**
	 * Renders canvas
	 *
	 * @returns {Canvas}
	 */
	render() {
		this._renderDisplayCanvas();
		return this;
	}

	/**
	 * Redraws the specified area and then render a whole scene
	 *
	 * @param {number} [x]
	 * @param {number} [y]
	 * @param {?number} [w]
	 * @param {?number} [h]
	 * @returns {Canvas}
	 */
	redrawArea(x = 0, y = 0, w = null, h = null) {
		this._updateWorkCanvas(x, y, w, h);
		this._renderDisplayCanvas();
		return this;
	}

	/**
	 * Sets Canvas Display Mode (see constants of this module)
	 *
	 * @param {number} mode
	 * @return {Canvas}
	 */
	setDisplayMode(mode) {
		this._displayMode = mode;
		this._updateWorkCanvas();
		this._calcScale();
		this.render();
		return this;
	}

	/**
	 * Returns Canvas Display Mode (see constants of this module)
	 *
	 * @returns {number}
	 */
	getDisplayMode() {
		return this._displayMode;
	}

	/**
	 * Sets current timeline frame
	 *
	 * @param {number} index
	 * @param {number} [selectFrom]
	 * @param {number} [selectTo]
	 * @return {Canvas}
	 */
	setFrame(index, selectFrom = -1, selectTo = -1) {
		if (this._sel.hasFragment) {
			this._sel.dropSelection();
			this.saveCurrentLayerImage();
			this._saveState('set-frame-when-fragment');
		}

		if (this._timeline.isSelectToEnd) {
			selectTo = this._timeline.frames - 1;
			selectFrom = selectFrom === -1 ? index : selectFrom;
		}

		if (selectFrom === -1 || selectTo === -1 || (selectFrom === index && selectTo === index)) {
			this.dropFramesSelection();
		} else {
			this.setFramesSelection(selectFrom, selectTo);
		}

		index = Math.max(0, Math.min(this._timeline.frames - 1, index));
		if (this._timeline.current === index) {
			return this;
		}

		this._timeline.current = index;
		for (let layer of this._layers.list) {
			layer.setFrame(index);
		}

		this._saveState('change-frame');

		this._updateShadowFrame();
		this._updateWorkCanvas();
		this._renderDisplayCanvas();
		this._events.trigger(EVENT_UPDATE_TIMELINE);
		return this;
	}

	/**
	 * Returns current frame index
	 *
	 * @returns {number}
	 */
	getCurrentFrame() {
		return this._timeline.current;
	}

	/**
	 * Returns total number of frames
	 *
	 * @returns {number}
	 */
	getTotalFrames() {
		return this._timeline.frames;
	}

	/**
	 * Sets selected frames range
	 *
	 * @param {number} [from]
	 * @param {number} [to]
	 * @returns {Canvas}
	 */
	setFramesSelection(from = -1, to = -1) {
		from = Math.max(0, Math.min(this._timeline.frames - 1, from));
		to = Math.max(0, Math.min(this._timeline.frames - 1, to));
		let f = Math.min(from, to),
			t = Math.max(from, to);
		if (this._timeline.fromFrame !== f || this._timeline.toFrame !== t) {
			this._timeline.fromFrame = f;
			this._timeline.toFrame = t;
			this._events.trigger(EVENT_UPDATE_TIMELINE);
		}
		return this;
	}

	/**
	 * Drops frames selection
	 *
	 * @returns {Canvas}
	 */
	dropFramesSelection() {
		if (this._timeline.fromFrame > -1) {
			this._timeline.fromFrame = -1;
			this._timeline.toFrame = -1;
			this._events.trigger(EVENT_UPDATE_TIMELINE);
		}
	}

	/**
	 * Returns currently selected frames
	 *
	 * @returns {{from: number, to: number}}
	 */
	getFramesSelection() {
		return {
			from: this._timeline.fromFrame,
			to: this._timeline.toFrame,
		}
	}

	/**
	 * Enables/disables mode when there is always selection from the current to the last frame
	 *
	 * @param {boolean} flag
	 * @returns {Canvas}
	 */
	enableSelectToEnd(flag) {
		this._timeline.isSelectToEnd = flag === true;

		if (this._timeline.isSelectToEnd) {
			this.setFrame(this._timeline.current, this._timeline.fromFrame, this._timeline.toFrame);
		} else {
			this.setFrame(this._timeline.current);
		}
		return this;
	}

	/**
	 * Returns TRUE if the "Select to End" mode is ON
	 *
	 * @returns {boolean}
	 */
	isSelectToEndEnabled() {
		return this._timeline.isSelectToEnd;
	}

	/**
	 * Enables/disables shadow frame in timeline
	 *
	 * @param {boolean} flag
	 * @returns {Canvas}
	 */
	enableShadowFrame(flag) {
		this._timeline.isShadowFrame = flag === true;
		this._updateShadowFrame();
		return this;
	}

	/**
	 * Returns TRUE if shadow frame enabled
	 *
	 * @returns {boolean}
	 */
	isShadowFrameEnabled() {
		return this._timeline.isShadowFrame;
	}

	/**
	 * Enables/disables grid
	 *
	 * @param {boolean} flag
	 * @returns {Canvas}
	 */
	enableGrid(flag) {
		this._render.grid = flag === true;
		this._renderDisplayCanvas();
		return this;
	}

	/**
	 * Returns TRUE if grid enabled
	 *
	 * @returns {boolean}
	 */
	isGridEnabled() {
		return this._render.grid;
	}

	/**
	 * Returns index of the shadow frame
	 *
	 * @returns {?number}
	 */
	getShadowFrame() {
		return this._timeline.shadowFrame;
	}

	/**
	 * Makes Key Frame in the current timeline position
	 *
	 * @returns {Canvas}
	 */
	makeKeyFrame() {
		let {from, to} = this._selectFramesForMakeKeyframe();
		this._layers.current.makeKeyFrame(from, to);
		if (this._timeline.isShadowFrame) {
			this._updateShadowFrame();
		}
		this._saveState('make_keyframe');
		this._events.trigger(EVENT_UPDATE_TIMELINE);
		return this;
	}

	/**
	 * Makes Blank Key Frame in the current timeline position
	 *
	 * @returns {Canvas}
	 */
	makeBlankKeyFrame() {
		let {from, to} = this._selectFramesForMakeKeyframe();
		this._layers.current.makeBlankKeyFrame(from, to);

		if (this._timeline.isShadowFrame) {
			this._updateShadowFrame();
		}
		this._updateLayersCanvases();
		this._updateWorkCanvas();
		this._renderDisplayCanvas();
		this._saveState('make_blank_keyframe');
		return this;
	}

	/**
	 * Returns correct frames for inserting new key frame (blank or filled)
	 *
	 * @return {{from: number, to: number}}
	 * @private
	 */
	_selectFramesForMakeKeyframe() {
		let from = this._timeline.fromFrame,
			to = this._timeline.toFrame;

		if (from !== to) {
			return {from, to};
		}

		if (from === -1) {
			from = this._timeline.current;
		}
		let status = this._layers.current.getFramesStatuses()[from];
		if (status !== 0 && status !== 2) {
			if (from + 1 >= this._timeline.frames) {
				return {from, to};
			}

			from++;

			status = this._layers.current.getFramesStatuses()[from];
			if (status !== 0 && status !== 2) {
				return {
					from: this._timeline.fromFrame,
					to: this._timeline.toFrame
				};
			}
			this.setFrame(from);
		}

		return {from, to: from};
	}

	/**
	 * Removes key frame OR frame from the current timeline position
	 *
	 * @returns {Canvas}
	 */
	removeFrame() {
		if (this._timeline.fromFrame === -1) {
			let frameStatus = this._layers.current.getCurrentFrameStatus();
			if (frameStatus === 0 || frameStatus === 2) {
				this._layers.current.removeFrame();
				if (this._timeline.isShadowFrame) {
					this._updateShadowFrame();
				}
				this._saveState('remove_frame');
			} else {
				this._layers.current.makeCleanFrame(
					this._timeline.fromFrame,
					this._timeline.toFrame
				);
				if (this._timeline.isShadowFrame) {
					this._updateShadowFrame();
				}
				this._saveState(frameStatus === 1 ? 'change_keyframe_to_frame' : 'change_blank_to_frame');
			}
			return this;
		}

		this._layers.current.removeFrame(
			this._timeline.fromFrame,
			this._timeline.toFrame
		);

		if (this._timeline.isShadowFrame) {
			this._updateShadowFrame();
		}

		this.dropFramesSelection();
		this._saveState('remove_frames');
		return this;
	}

	/**
	 * Inserts new repeat/empty frame to current position and shifts all frames at right to 1 step
	 *
	 * @returns {Canvas}
	 */
	insertFrame() {
		if (!this._layers.current.canInsertFrame()
			|| this._timeline.fromFrame > -1) {
			return this;
		}
		this._layers.current.insertFrame();
		this._saveState('insert_frame');
		return this;
	}

	/**
	 * @returns {Canvas}
	 */
	invertFrames() {
		this._layers.current.invertFrames();
		this._saveState('invert_frames');
		return this;
	}

	/**
	 * Changes keyframe position
	 *
	 * @param {number} fromIndex
	 * @param {number} toIndex
	 * @return {Canvas}
	 */
	moveKeyFrame(fromIndex, toIndex) {
		let frameStatus = this._layers.current.getFramesStatuses()[fromIndex];
		if (frameStatus === 0 || frameStatus === 2) {
			return this;
		}
		this._layers.current.moveKeyFrame(fromIndex, toIndex);
		this.setFrame(toIndex);
		this._saveState('move_frame');
		return this;
	}

	/**
	 * Repeats current list of frames
	 *
	 * @param {number} [times=0]
	 * @param {boolean} [isReflected=false]
	 * @returns {Canvas}
	 */
	repeatFrames(times = 0, isReflected = false) {
		this._layers.current.repeatFrames(this._timeline.fromFrame, this._timeline.toFrame, times, isReflected);
		this._saveState('repeat_frames');
		return this;
	}

	/**
	 * @param {AbstractCanvasTransformTool} transformer
	 * @return {Promise<void>}
	 */
	async transformLayer(transformer) {
		transformer.setSceneSize(this._sceneWidth, this._sceneHeight)
			.setDataSet(this._dataSet)
			.setCanvasSelection(this._sel);

		this._events.trigger(EVENT_PROCESS_START);
		this._sel.releaseFragment();

		await transformer.applyForLayer(this, this._layers.current, this._timeline.fromFrame, this._timeline.toFrame);

		this._sel.dropSelection();
		this._updateLayersCanvases();
		this._updateWorkCanvas();
		this._renderDisplayCanvas();
		this._saveState(transformer.actionName);
		this._events.trigger(EVENT_PROCESS_FINISH);
	}

	/**
	 * Returns list of layers
	 *
	 * @returns {Array<CanvasLayer>}
	 */
	getLayers() {
		return this._layers.list;
	}

	/**
	 * Adds new layer before current layer
	 *
	 * @param {T_CanvasLayerOptions} [options]
	 * @param {boolean} [prepareLayerTranslation]
	 * @returns {CanvasLayer}
	 */
	addLayer(options = {}, prepareLayerTranslation = false) {
		if (!options.data) {
			options.data = {};
		}
		if (!options.data.title) {
			let takenNames = [], name, i = 1;
			this._layers.list.forEach(l => takenNames.push(l.getTitle()));
			do {
				name = 'Layer #' + (i++);
			} while (takenNames.indexOf(name) > -1);
			options.data.title = name;
		}
		let layer = this._createLayer(options, true, true);
		this.selectLayer(layer);

		if (prepareLayerTranslation) {
			this.setTool('translate');
			this.setFramesSelection(0, this._timeline.frames - 1);
		}

		this._saveState('add-layer');
		this.updateCursor();
		return layer;
	}

	/**
	 * Creates new layer
	 *
	 * @param {T_CanvasLayerOptions} [options]
	 * @param {boolean} [triggerEvent]
	 * @param {boolean} [insertBeforeCurrent]
	 * @returns {CanvasLayer}
	 */
	_createLayer(options = {}, triggerEvent = true, insertBeforeCurrent = false) {
		let layer = new CanvasLayer(this, options);
		layer.events.addListener(EVENT_FRAME_LOADED, this._handlers.layerImageUpdatedLazy);
		layer.events.addListener(EVENT_VISIBILITY_CHANGED, this._handlers.layerStateChanged);
		layer.setFrame(this._timeline.current);
		if (insertBeforeCurrent && this._layers.current) {
			// insert layer before current one
			this._layers.list.splice(this._layers.list.indexOf(this._layers.current), 0, layer);
		} else {
			this._layers.list.push(layer);
		}
		triggerEvent && this._events.trigger(EVENT_UPDATE_TIMELINE);
		if (this._layers.current === null) {
			this.selectLayer(layer);
		}
		return layer;
	}

	/**
	 * Creates clone of the current layer
	 *
	 * @param {CanvasLayer} layer
	 * @returns {boolean}
	 */
	async duplicateCurrentLayer(layer) {
		let data = await this._layers.current.getData(true, false);
		this.addLayer({data}, true);
		return true;
	}

	/**
	 * Removes layer
	 *
	 * @param {CanvasLayer} layer
	 * @returns {boolean}
	 */
	removeLayer(layer) {
		if (this._layers.list.length === 1) {
			return false;
		}
		let i = this._layers.list.indexOf(layer);
		if (i === -1) {
			return false;
		}
		this._layers.list.splice(i, 1);
		this._trash.layers.set(layer.getId(), layer);
		if (this._layers.current === layer) {
			if (this._layers.list.length > 0) {
				if (this._layers.list[i]) {
					this.selectLayer(this._layers.list[i]);
				} else {
					this.selectLayer(this._layers.list[i - 1]);
				}
			}
		} else {
			this._events.trigger(EVENT_UPDATE_TIMELINE);
		}
		this._saveState('remove-layer');
		this._updateLayersCanvases();
		this.updateCursor();
		this._updateWorkCanvas();
		this._renderDisplayCanvas();
		return true;
	}

	/**
	 * Merges current layer with the next one
	 *
	 * @returns {boolean}
	 */
	mergeCurrentLayerWithNext() {
		if (this._layers.list.length === 1) {
			return false;
		}
		let slaveLayer = this._layers.current;
		let i = this._layers.list.indexOf(slaveLayer);
		if (i === -1 || i === this._layers.list.length - 1) {
			return false;
		}
		this._events.trigger(EVENT_PROCESS_START);
		this._layers.list.splice(i, 1);
		this._trash.layers.set(slaveLayer.getId(), slaveLayer);
		// select next layer
		this.selectLayer(this._layers.list[i]);
		// merge slave layers into master
		this._layers.current.mergeLayer(slaveLayer).then(() => {
			this._saveState('merge-layer');
			this._updateLayersCanvases();
			this._updateWorkCanvas();
			this._renderDisplayCanvas();
			this._events.trigger(EVENT_UPDATE_TIMELINE);
			this._events.trigger(EVENT_PROCESS_FINISH);
		});
		this.updateCursor();
		return true;
	}

	/**
	 * Changes order of layers
	 *
	 * @param {Array<CanvasLayer>} layers
	 * @returns {Canvas}
	 */
	rearrangeLayersOrder(layers) {
		this._layers.list.sort((a, b) => layers.indexOf(a) - layers.indexOf(b));
		this._events.trigger(EVENT_UPDATE_TIMELINE);
		this._saveState('change-layers-order');
		this._updateLayersCanvases(true);
		this._updateWorkCanvas();
		this._renderDisplayCanvas();
		return this;
	}

	/**
	 * Selects current layer
	 *
	 * @param {CanvasLayer} layer
	 * @return {Canvas}
	 */
	selectLayer(layer) {
		if (this._layers.current === layer) {
			return this;
		}

		let prevLayer = this._layers.current;
		this._layers.current = layer;
		this.dropFramesSelection();

		this._saveState('change-frame');

		this._updateLayersCanvases();
		if (prevLayer !== null) {
			this._events.trigger(EVENT_UPDATE_TIMELINE);
		}
		this.updateCursor();
		return this;
	}

	/**
	 * Returns current layer model
	 *
	 * @return {null|CanvasLayer}
	 */
	getCurrentLayer() {
		return this._layers.current;
	}

	/**
	 * Saves image from Layer Canvas to Layer Model
	 *
	 * @param {boolean} forceKey Make keyframe by force
	 * @returns {Canvas}
	 */
	saveCurrentLayerImage(forceKey = false) {
		if (!this._layers.current) {
			return this;
		}
		let frameStatus = this._layers.current.getCurrentFrameStatus();
		let dataURL = this._layerCanvas.el.toDataURL('image/png');
		if (dataURL === this._blankImageData) {
			dataURL = '';
		}

		if (dataURL === '' && !this._sel.hasFragment) {
			if (frameStatus === 1) {
				// keyframe, make a blank frame
				this._layers.current.makeBlankKeyFrame();
				if (this._timeline.isShadowFrame) {
					this._updateShadowFrame();
				}
			} else if (forceKey && frameStatus !== 3) {
				// make a blank key frame by force
				this._layers.current.makeBlankKeyFrame();
				if (this._timeline.isShadowFrame) {
					this._updateShadowFrame();
				}
			}
		} else {
			// create keyframe
			this._layers.current.setImage(dataURL);
			if (this._timeline.isShadowFrame) {
				this._updateShadowFrame();
			}
		}
		return this;
	}

	/**
	 * Handles image update in the layer model (lazy mode)
	 *
	 * @private
	 * @returns {void}
	 */
	_onLayerImageUpdatedLazy() {
		clearTimeout(this._loadImagesTimerId);
		this._loadImagesTimerId = setTimeout(this._handlers.layerImageUpdated, 0);
	}

	/**
	 * Handles image update in the layer model
	 *
	 * @private
	 * @returns {void}
	 */
	_onLayerImageUpdated() {
		this._updateLayersCanvases();
		this._updateWorkCanvas();
		this._renderDisplayCanvas();
	}

	/**
	 * Handles layer state change (visibility or other props)
	 *
	 * @private
	 * @returns {void}
	 */
	_onLayerStateChanged() {
		this._updateLayersCanvases();
		this._updateWorkCanvas();
		this._renderDisplayCanvas();
	}

	/**
	 * Updates canvases of the layers
	 *
	 * @private
	 * @param {boolean} [force=false]
	 * @returns {void}
	 */
	_updateLayersCanvases(force = false) {
		let el, ctx, layers = this._layers;
		let curIdx = layers.list.indexOf(layers.current);
		let layersHash = '';

		for (let l of layers.list) {
			if (!l.isLoaded()) {
				// not all layers ready to render
				return;
			}
			if (l !== layers.current) {
				layersHash += ',' + l.getImageKey()
					+ ':' + l.getOpacity()
					+ ':' + l.getBlendingMode()
					+ ':' + (l.isVisible() ? 'v' : 'h');
			}
		}

		// update current layer
		el = this._layerCanvas.el;
		ctx = this._layerCanvas.ctx;
		ctx.clearRect(0, 0, el.width, el.height);
		layers.current.drawFrame(this._layerCanvas, null, false);

		if (layers.lastImagesHash !== layersHash || force) {
			layers.lastImagesHash = layersHash;
			// NOTE! we merge layers in different direction: upper will be first layer
			// update canvas with all layers under current one
			el = this._layersUnder.el;
			ctx = this._layersUnder.ctx;
			ctx.clearRect(0, 0, el.width, el.height);
			for (let i = layers.list.length - 1; i > curIdx; i--) {
				let layer = layers.list[i];
				if (layer.isVisible()) {
					layer.drawFrame(this._layersUnder);
				}
			}

			// update canvas with all layers above current one
			this._layersAboveRenderList = [];
			const list = this._layersAboveRenderList;
			const pool = this._layersAboveCanvasesPool;
			let poolIdx = -1;

			for (let i = curIdx - 1; i >= 0; i--) {
				let layer = layers.list[i];
				if (layer.getBlendingMode() !== CanvasBlendingMode.NORMAL) {
					if (layer.isVisible()) {
						list.push(layer);
					}
					continue;
				}

				if (layer.isVisible()) {
					if (list.length === 0 || list[list.length - 1] instanceof CanvasLayer || poolIdx === -1) {
						poolIdx++;
						if (!pool[poolIdx]) {
							pool[poolIdx] = Utils.createCanvas('', this._sceneWidth, this._sceneHeight);
						}

						list.push(pool[poolIdx]);
						pool[poolIdx].ctx.clearRect(0, 0, this._sceneWidth, this._sceneHeight);
					}

					layer.drawFrame(pool[poolIdx]);
				}
			}
		}
		// restore alpha up to 100%
		ctx.globalAlpha = 1;
	}

	/**
	 * Saves current scene state to history
	 *
	 * @param {string} type
	 * @returns {void}
	 * @private
	 */
	_saveState(type) {
		let hist = this._history;
		let curRec = hist.records[hist.index - 1];

		if (type === 'change-frame' && curRec && curRec.type === type) {
			// just update current frame
			this._saveLayersState(curRec);
			this._sel.saveState(curRec);
			return;
		}

		if (type !== 'change-frame' && curRec && curRec.frame !== this._timeline.current) {
			// current frame and frame in last action are different
			// clone previous state and set in it a current frame index, because of issue with Ctrl+Z
			// noinspection JSValidateTypes
			/** @type {T_State} newRec */
			let newRec = Object.create(curRec);
			newRec.frame = this._timeline.current;
			hist.records.push(newRec);
			hist.index++;
		}

		if (hist.index < hist.records.length) {
			// remove all history after the current position
			hist.records.splice(hist.index);
		}

		hist.index++;

		/** @type {T_State} */
		let rec = {
			type,
			id: Date.now(),
			frame: 0,
			frameSelection: null,
			layer: '',
			layers: [],
			path: null,
			selection: null,
			isSaved: false
		};
		this._saveLayersState(rec);
		this._sel.saveState(rec);
		this._draw.saveState(rec);
		hist.records.push(rec);

		if (hist.records.length > LIMIT_MAX_HISTORY_RECORDS) {
			let cut = hist.records.splice(0, hist.records.length - LIMIT_MAX_HISTORY_RECORDS);
			hist.index -= cut.length;
		}

		if (++this._trash.gcSequence === 5) {
			this._trash.gcSequence = 0;
			this.collectGarbage();
		}

		this._events.trigger(EVENT_HISTORY_CHANGED);
	}

	/**
	 * Restores scene state from the history

	 * @returns {void}
	 * @private
	 */
	_restoreState() {
		let hist = this._history;
		let rec = hist.records[hist.index - 1];
		this._cursor.isTraceNextPoint = false;
		this._restoreLayersState(rec);
		this._sel.restoreState(rec).then(() => {
			this._renderDisplayCanvas();
		});
		this._draw.restoreState(rec);
		this._events.trigger(EVENT_UPDATE_TIMELINE);
		this._updateLayersCanvases();
		this._updateWorkCanvas();
		this._renderDisplayCanvas();
		this._events.trigger(EVENT_HISTORY_CHANGED);
	}

	/**
	 * Saves state of all layers for History
	 *
	 * @param {T_State} rec
	 * @returns {void}
	 * @private
	 */
	_saveLayersState(rec) {
		let layers = [];
		for (let layer of this._layers.list) {
			layers.push(layer.getState());
		}
		rec.layer = this._layers.current.getId();
		rec.frame = this._timeline.current;
		rec.frameSelection = {
			from: this._timeline.fromFrame,
			to: this._timeline.toFrame
		};
		rec.layers = layers;
	}

	/**
	 * Restores state of all layers
	 *
	 * @param {T_State} rec
	 * @returns {void}
	 * @private
	 */
	_restoreLayersState(rec) {
		let newLayersList = [], map = new Map();
		for (let layer of this._layers.list) {
			map.set(layer.getId(), layer);
		}

		/** @type {CanvasLayer} */
		let layer;
		let curLayer;
		for (let layerState of rec.layers) {
			if (map.has(layerState.id)) {
				// restore state for existing layer
				layer = map.get(layerState.id);
				map.delete(layerState.id);
			} else {
				// restore layer from the trash
				layer = this._trash.layers.get(layerState.id);
				this._trash.layers.delete(layerState.id);
			}
			if (layerState.id === rec.layer) {
				curLayer = layer;
			}
			layer.restoreState(layerState);
			newLayersList.push(layer);
		}

		this._layers.list = newLayersList;

		if (rec.frame !== this._timeline.current) {
			this.setFrame(rec.frame);
		}

		if (curLayer !== this._layers.current) {
			this.selectLayer(curLayer);
		}

		for (let [id, layer] of map.entries()) {
			this._trash.layers.set(id, layer);
		}
		if (rec.frameSelection.from !== this._timeline.fromFrame
			|| rec.frameSelection.to !== this._timeline.toFrame) {
			if (rec.frameSelection.from === -1) {
				this.dropFramesSelection();
			} else {
				this.setFramesSelection(
					rec.frameSelection.from,
					rec.frameSelection.to
				);
			}
		}
	}

	/**
	 * Undo last action in the history
	 *
	 * @returns {boolean}
	 */
	undo() {
		let hist = this._history;
		if (hist.index <= 1) {
			return false;
		}
		hist.index--;
		this._restoreState();
		return true;
	}

	/**
	 * Redo action in the history
	 *
	 * @returns {boolean}
	 */
	redo() {
		let hist = this._history;
		if (hist.index >= hist.records.length) {
			return false;
		}
		hist.index++;
		this._restoreState();
		return true;
	}

	/**
	 * Sets canvas size
	 *
	 * @param {number} width
	 * @param {number} height
	 * @returns {Canvas}
	 */
	setSize(width, height) {
		this._width = +width;
		this._height = +height;

		const canvas = this._displayCanvas.el;
		canvas.width = this._width * this._dprFactor;
		canvas.height = this._height * this._dprFactor;
		canvas.style.width = `${this._width}px`;
		canvas.style.height = `${this._height}px`;
		
		this._cursor.offsetTop = canvas.getBoundingClientRect().top;
		this._sel.setWorkAreaSize(this._width * this._dprFactor, this._height * this._dprFactor);
		this._calcScale();
		this._renderDisplayCanvas();
		return this;
	}

	/**
	 * Adds events listeners
	 *
	 * @private
	 * @returns {void}
	 */
	_listenEvents() {
		this._renewEventListeners();
		this._colorsSet.addListener(EVENT_CHANGE_COLOR, () => {
			this.setColor(
				this._colorsSet.getCurrentColor(),
				this._colorsSet.getCurrentOpacity()
			);
		});
		this._neighborScenes.addListener(EVENT_NEIGHBOR_CHANGED, () => {
			this._renderDisplayCanvas();
		});
	}

	/**
	 * @private
	 */
	_renewEventListeners() {
		const el = this._displayCanvas.el;
		el.removeEventListener('contextmenu', this._handlers.contextMenu);
		el.removeEventListener('mousedown', this._handlers.mouseDown);
		el.removeEventListener('pointermove', this._handlers.mouseMoveShadow);
		el.removeEventListener('touchstart', this._handlers.touchDown);
		el.removeEventListener('mousewheel', this._handlers.mouseWheel);
		el.removeEventListener('MozMousePixelScroll', this._handlers.mouseWheel);
		document.removeEventListener('keydown', this._handlers.keyDown);
		document.removeEventListener('keyup', this._handlers.keyUp);
		window.removeEventListener('blur', this._handlers.cancelAllPressedKeys);

		el.addEventListener('contextmenu', this._handlers.contextMenu);
		el.addEventListener('mousedown', this._handlers.mouseDown);
		el.addEventListener('pointermove', this._handlers.mouseMoveShadow);
		el.addEventListener('touchstart', this._handlers.touchDown);
		el.addEventListener('mousewheel', this._handlers.mouseWheel);
		el.addEventListener('MozMousePixelScroll', this._handlers.mouseWheel);
		document.addEventListener('keydown', this._handlers.keyDown);
		document.addEventListener('keyup', this._handlers.keyUp);
		window.addEventListener('blur', this._handlers.cancelAllPressedKeys);
	}

	/**
	 * Sets current fill color
	 *
	 * @param {Color|string} color
	 * @param {?number} [opacity]
	 * @return {Canvas}
	 */
	setColor(color, opacity = null) {
		let rgb = (color instanceof Color) ? color.values : Color.create(color).values;
		let newColor = this._palette.convertColor(rgb[0], rgb[1], rgb[2]);
		this._colorsSet.changeCurrentSlotColor(newColor.rgbCss);
		this._fill = Color.create(newColor.rgbCss, opacity !== null ? (opacity * 255) | 0 : this._fill.alpha);
		this._calcBrush();
		this._events.trigger(EVENT_SET_COLOR, this._fill);
		return this;
	}

	/**
	 * Sets scale
	 *
	 * @param {number} factor
	 * @returns {Canvas}
	 */
	setScale(factor) {
		factor = LIMIT_SCALE_FACTORS.reduce((prev, curr) => Math.abs(factor - curr) < Math.abs(factor - prev) ? curr : prev);
		this._scale.factor = factor;
		this._calcScale();
		this._calcBrush();
		this._renderDisplayCanvas();
		return this;
	}

	/**
	 * Calculates scale properties
	 *
	 * @private
	 * @returns {void}
	 */
	_calcScale() {
		const displayCanvas = this._displayCanvas.el;
		const scale = this._scale;
		const workCanvas = this._workCanvas.el;

		const noDrpDisplayWidth = displayCanvas.width / this._dprFactor;
		const noDrpDisplayHeight = displayCanvas.height / this._dprFactor;

		scale.srcWidth = noDrpDisplayWidth / scale.factor;
		scale.srcHeight = noDrpDisplayHeight / scale.factor;

		const hw = scale.srcWidth / 2;
		const hh = scale.srcHeight / 2;

		scale.srcX = scale.centerX - hw;
		scale.srcY = scale.centerY - hh;

		scale.srcToDistWidth = scale.srcWidth / noDrpDisplayWidth;
		scale.srcToDistHeight = scale.srcHeight / noDrpDisplayHeight;

		scale.dstWidth = workCanvas.width * scale.factor;
		scale.dstHeight = workCanvas.height * scale.factor;
		scale.dstX = noDrpDisplayWidth / 2 - scale.centerX * scale.factor;
		scale.dstY = noDrpDisplayHeight / 2 - scale.centerY * scale.factor;
	}

	/**
	 * Returns scale factor
	 *
	 * @returns {number}
	 */
	getScale() {
		return this._scale.factor;
	}

	/**
	 * @param {number} angle
	 */
	setToolAngle(angle) {
		this._currentDrawTool.angle = Math.min(90, Math.max(0, angle));
		if (this._ellipseDrawer) {
			this._ellipseDrawer.setAngle(this._currentDrawTool.angle);
		}
		this.updateCursor();
	}

	/**
	 * Sets current tool
	 *
	 * @param {string} tool
	 * @returns {Canvas}
	 */
	setTool(tool) {
		let cursor = this._cursor;

		if (this._tool === tool) {
			return this;
		}

		this._tool = tool;
		cursor.onMove = null;
		cursor.onDown = null;
		cursor.onUp = null;

		switch (this._tool) {
			case 'cursor':
				break;
			case 'erase':
			case 'brush':
			case 'brush_bg':
				cursor.onDown = this._initDrawLine;
				cursor.onMove = this._drawLine;
				cursor.onUp = this._finishDrawLine;
				break;
			case 'move':
				cursor.onDown = this._initViewportMoving;
				cursor.onMove = this._moveViewport;
				break;
			case 'translate':
				cursor.onDown = this._initSceneTranslation;
				cursor.onMove = this._translateScene;
				cursor.onUp = this._processSceneTranslation;
				break;
			case 'ellipse':
				cursor.onDown = this._initDrawEllipse;
				cursor.onMove = this._drawEllipse;
				cursor.onUp = this._finishDrawEllipse;
				break;
			case 'rect':
			case 'lasso':
			case 'poly_lasso':
				cursor.onDown = this._initNewSelection;
				cursor.onMove = this._saveSelectionPoint;
				cursor.onUp = this._processNewSelection;
				break;
			case 'picker':
				cursor.onDown = this.pickColor;
				cursor.onMove = this.pickColor;
				break;
			case 'zoom':
				cursor.onUp = this._zoomByClick;
				break;
			case 'fill':
				cursor.onDown = this._fillByClick;
				break;
		}

		if (this._brush.sizeByTools.has(this._tool)) {
			// set last used brush size for current tool
			this.setBrush(this._brush.sizeByTools.get(this._tool));
		} else if (typeof LIMIT_BRUSH_MIN_SIZES[this._tool] === 'number') {
			// set default brush size for current tool
			this.setBrush(LIMIT_BRUSH_MIN_SIZES[this._tool]);
		}
		this.updateCursor();
		this._renderDisplayCanvas();
		this._events.trigger(EVENT_CHANGE_TOOL, this._tool);
		return this;
	}

	/**
	 * @private
	 */
	_saveCurrentDrawToolProps() {
		this._currentDrawTool.tool = this._tool;
		this._currentDrawTool.brushSize = this._brush.size;
		if (this._tool === 'erase') {
			this._currentDrawTool.fill = new Color(0, 0, 0, this._fill.alpha);
		} else {
			this._currentDrawTool.fill = this._fill;
		}
	}

	/**
	 * Initializes draw line tool
	 *
	 * @param {number} x
	 * @param {number} y
	 * @returns {void}
	 * @private
	 */
	_initDrawLine(x, y) {
		if (!this._layers.current.isOperable) {
			return;
		}

		this._saveCurrentDrawToolProps();
		this._sel.releaseFragment();
		this._clearTempCanvas(true);
		this._draw.initNewLine(
			this._tempCanvas.ctx,
			this._convertSourceXY(x, y),
			this._currentDrawTool.fill,
			this._currentDrawTool.brushSize,
			this._cursor.isShift
		);
	}

	/**
	 * Draws line
	 *
	 * @param {number} x
	 * @param {number} y
	 * @returns {void}
	 * @private
	 */
	_drawLine(x, y) {
		if (!this._layers.current.isOperable) {
			return;
		}

		let ctx = this._tempCanvas.ctx;

		this._sel.applyClip(ctx, () => {
			this._clearTempCanvas();
			this._draw.draw(
				ctx,
				this._convertSourceXY(x, y),
				this._currentDrawTool.brushSize,
				this._cursor.isShift
			);
		});

		if (this._draw.isBufferOverflow()) {
			this._tempBufferCanvas.ctx.drawImage(this._tempCanvas.el, 0, 0);
			this._clearTempCanvas();
			this._draw.clearBuffer();
		}

		this._drawTemp2WorkCanvas(this._draw.lastDrawnRect);
		this._renderDisplayCanvas();
		this._events.trigger(EVENT_DRAWING);
	}

	/**
	 * Finishes line drawing and saves result
	 *
	 * @private
	 * @returns {void}
	 */
	_finishDrawLine() {
		if (!this._layers.current.isOperable) {
			return;
		}

		let rect = this._draw.finish();
		this._mergeTemp2LayerCanvas();
		this.saveCurrentLayerImage();
		this._saveState('draw-brush');
		this._updateWorkCanvas(
			rect.left - 1,
			rect.top - 1,
			rect.right - rect.left + 2,
			rect.bottom - rect.top + 2
		);
		this._renderDisplayCanvas();
		this._events.trigger(EVENT_DRAWING);
	}

	/**
	 * Initializes tool for drawing ellipse/circle
	 *
	 * @param {number} x
	 * @param {number} y
	 * @returns {void}
	 * @private
	 */
	_initDrawEllipse(x, y) {
		if (!this._layers.current.isOperable) {
			return;
		}

		this._saveCurrentDrawToolProps();
		this._sel.releaseFragment();
		this._clearTempCanvas(true);
		this._ellipseDrawer = new CanvasEllipse(
			this._tempCanvas.ctx,
			this._fill,
			this._convertSourceXY(x, y),
			this._brush.size,
			this._currentDrawTool.angle
		);
	}

	/**
	 * Draws ellipse
	 *
	 * @param {number} x
	 * @param {number} y
	 * @returns {void}
	 * @private
	 */
	_drawEllipse(x, y) {
		if (!this._layers.current.isOperable) {
			return;
		}

		this._sel.applyClip(this._tempCanvas.ctx, () => {
			this._clearTempCanvas();
			this._ellipseDrawer.draw(
				this._convertSourceXY(x, y),
				this._cursor.isShift
			);
		});

		this._drawTemp2WorkCanvas(this._ellipseDrawer.lastDrawnRect);
		this._renderDisplayCanvas();
		this._events.trigger(EVENT_DRAWING);
	}

	/**
	 * Finishes line drawing and saves result
	 *
	 * @private
	 * @returns {void}
	 */
	_finishDrawEllipse() {
		if (!this._layers.current.isOperable) {
			return;
		}

		this._mergeTemp2LayerCanvas();
		this.saveCurrentLayerImage();
		this._saveState('draw-ellipse');
		this._updateWorkCanvas();
		this._renderDisplayCanvas();
		this._events.trigger(EVENT_DRAWING);
	}

	/**
	 * Initializes new scene translation
	 *
	 * @param {number} x
	 * @param {number} y
	 * @returns {void}
	 * @private
	 */
	_initSceneTranslation(x, y) {
		let pos = this._convertSourceXY(x, y);
		this._translate = new CanvasTranslate(this._dataSet, this._sceneWidth, this._sceneHeight, pos.x, pos.y);
	}

	/**
	 * Translates scene in preview mode
	 *
	 * @param {number} x
	 * @param {number} y
	 * @returns {void}
	 * @private
	 */
	_translateScene(x, y) {
		if (this._translate === null) {
			return;
		}

		let pos = this._convertSourceXY(x, y);
		this._translate.translateTo(pos.x, pos.y);
		this._updateWorkCanvas();
		this._renderDisplayCanvas();
		this._events.trigger(EVENT_DRAWING);
	}

	/**
	 * Applies scene translation
	 *
	 * @returns {void}
	 * @private
	 */
	_processSceneTranslation() {
		if (this._translate === null) {
			return;
		}

		this._events.trigger(EVENT_PROCESS_START);
		this._translate.applyForLayer(
			this._layers.current, this._timeline.fromFrame, this._timeline.toFrame
		).then(() => {
			this._translate = null;
			this._updateLayersCanvases();
			this._saveState('translate');
			this._events.trigger(EVENT_PROCESS_FINISH);
			this._events.trigger(EVENT_DRAWING);
		});
	}

	/**
	 * Returns current tool Name
	 *
	 * @returns {string}
	 */
	getTool() {
		return this._tool;
	}

	/**
	 * Sets current brush
	 *
	 * @param {number} size
	 * @returns {Canvas}
	 */
	setBrush(size) {
		size = Math.max(LIMIT_BRUSH_MIN_SIZES[this._tool] || 1, size);
		size = Math.min(LIMIT_BRUSH_MAX_SIZES[this._tool] || 1, size);
		this._brush.size = size;
		this._calcBrush();
		this._brush.sizeByTools.set(this._tool, size);
		this.events.trigger(EVENT_SET_BRUSH);
		return this;
	}

	/**
	 * Returns current brush size
	 *
	 * @returns {number}
	 */
	getBrushSize() {
		return this._brush.size;
	}

	/**
	 * Calculates brush properties
	 *
	 * @private
	 * @returns {void}
	 */
	_calcBrush() {
		this._brush.displayHalfSize = this._brush.size / 2 * this._scale.factor;
		this.updateCursor();
	}

	/**
	 * Picks color at the specified position
	 *
	 * @param {number} x
	 * @param {number} y
	 * @returns {Canvas}
	 */
	pickColor(x, y) {
		let pos = this._convertSourceXY(x, y),
			workCanvas = this._workCanvas.el;
		x = Math.max(0, Math.min(workCanvas.width - 1, Math.round(pos.x)));
		y = Math.max(0, Math.min(workCanvas.height - 1, Math.round(pos.y)));
		let color = this._workCanvas.ctx.getImageData(x, y, 1, 1).data;
		let newColor = this._palette.convertColor(color[0], color[1], color[2]);
		this.setColor(newColor.rgbCss);
		return this;
	}

	/**
	 * Makes flood filling into the specified position
	 *
	 * @private
	 * @returns {void}
	 */
	_fillByClick(x, y) {
		if (!this._layers.current.isOperable) {
			return;
		}
		let res;

		if (this._sel.hasSelection && this._sel.isPointerOverSelection) {
			res = this._sel.fill(this._fill);
		} else {
			let pos = this._convertSourceXY(x, y);
			res = (new CanvasFloodFill(this._layerCanvas))
				.fill(pos.x, pos.y, this._fill);
		}

		if (!res) {
			return;
		}
		this.saveCurrentLayerImage();
		this._saveState('fill');

		this._updateWorkCanvas();
		this._renderDisplayCanvas();
		this._events.trigger(EVENT_DRAWING);
	}

	/**
	 * Makes zoom in/out by click at the point
	 *
	 * @private
	 * @param {number} x
	 * @param {number} y
	 * @returns {void}
	 */
	_zoomByClick(x, y) {
		this.zoomPointByXY(this._convertClientXY(x, y), !this._cursor.isAlt);
	}

	/**
	 * Zooms in/out point by client (browsers) x and y coordinates
	 *
	 * @param {T_Point} pos
	 * @param {boolean} isZoomIn
	 * @returns {Canvas}
	 */
	zoomPointByXY(pos, isZoomIn = true) {
		const scale = this._scale;
		let factor = scale.factor;

		const displayCanvas = this._displayCanvas.el;
		const dx = pos.x - ((displayCanvas.width / this._dprFactor) / 2);
		const dy = pos.y - ((displayCanvas.height / this._dprFactor) / 2);

		pos = this._convertSourceXY(pos.x, pos.y);
		if (isZoomIn) {
			factor = LIMIT_SCALE_FACTORS[Math.min(LIMIT_SCALE_FACTORS.length - 1, LIMIT_SCALE_FACTORS.indexOf(factor) + 1)];
		} else {
			factor = LIMIT_SCALE_FACTORS[Math.max(0, LIMIT_SCALE_FACTORS.indexOf(factor) - 1)];
		}

		scale.factor = factor;
		scale.centerX = pos.x - dx / factor;
		scale.centerY = pos.y - dy / factor;
		this._calcScale();
		this._calcBrush();
		this._renderDisplayCanvas();
		return this;
	}

	/**
	 * Handles key down event
	 *
	 * @param {KeyboardEvent} e
	 * @returns {void}
	 * @private
	 */
	_onKeyDown(e) {
		switch (e.code) {
			case 'Shift':
			case 'ShiftLeft':
			case 'ShiftRight':
				this._cursor.isShift = true;
				this.updateCursor();
				this._renderDisplayCanvas();
				break;
			case 'Control':
			case 'ControlLeft':
			case 'ControlRight':
			case 'Meta':
			case 'MetaLeft':
			case 'MetaRight':
				this._cursor.isCtrl = true;
				this.updateCursor();
				break;
			case 'Alt':
			case 'AltLeft':
			case 'AltRight':
				e.preventDefault();
				this._cursor.isAlt = true;
				this.updateCursor();
				break;
		}
	}

	/**
	 * Handles key up event
	 *
	 * @param {KeyboardEvent} e
	 * @returns {void}
	 * @private
	 */
	_onKeyUp(e) {
		switch (e.code) {
			case 'Shift':
			case 'ShiftLeft':
			case 'ShiftRight':
				this._cursor.isShift = false;
				this.updateCursor();
				this._renderDisplayCanvas();
				break;
			case 'Control':
			case 'ControlLeft':
			case 'ControlRight':
			case 'Meta':
			case 'MetaLeft':
			case 'MetaRight':
				this._cursor.isCtrl = false;
				this.updateCursor();
				break;
			case 'Alt':
			case 'AltLeft':
			case 'AltRight':
				e.preventDefault();
				this._cursor.isAlt = false;
				this.updateCursor();
				break;
		}
	}

	/**
	 * @private
	 */
	_cancelAllPressedKeys() {
		this._cursor.isCtrl = false;
		this._cursor.isAlt = false;
		this._cursor.isShift = false;
		this.updateCursor();
	}

	/**
	 * Converts display canvas X and Y to work canvas coordinate system (remove scaling)
	 *
	 * @param {number} x
	 * @param {number} y
	 * @returns {T_Point}
	 * @private
	 */
	_convertSourceXY(x, y) {
		let scale = this._scale;
		x = scale.srcX + x * scale.srcToDistWidth;
		y = scale.srcY + y * scale.srcToDistHeight;
		return {x, y};
	}

	/**
	 * Converts client X and Y to work canvas coordinate system (remove scaling)
	 *
	 * @param {number} x
	 * @param {number} y
	 * @returns {T_Point}
	 * @private
	 */
	_convertClientXY(x, y) {
		const bBox = this._displayCanvas.el.getBoundingClientRect();
		return {
			x: x - bBox.left,
			y: y - bBox.top
		};
	}

	/**
	 * Sets correct cursor for the viewport
	 *
	 * @returns {Canvas}
	 */
	updateCursor() {
		const cursor = this._cursor;
		const classList = this._displayCanvas.el.classList;
		let cssClass = '';

		switch (this._tool) {
			default:
			case 'cursor':
				cssClass = 'canvas_tool_default';
				break;
			case 'brush':
			case 'brush_bg':
			case 'erase':
				cssClass = !this._layers.current.isOperable ? 'canvas_tool_not-operable' : 'canvas_tool_draw';
				this._cursor.circleEl.setAttribute('r', String(this._brush.displayHalfSize));
				this._cursor.circleBackEl.setAttribute('r', String(this._brush.displayHalfSize));
				const opacity = Math.round(this._colorsSet.getCurrentOpacity() * 100);
				if (opacity < 100) {
					this._cursor.textEl.textContent = `${opacity}%`;
					this._cursor.textBackEl.textContent = `${opacity}%`;
				} else {
					this._cursor.textEl.textContent = '';
					this._cursor.textBackEl.textContent = '';
				}
				break;
			case 'picker':
				cssClass = 'canvas_tool_draw';
				this._cursor.circleEl.setAttribute('r', '1');
				this._cursor.circleBackEl.setAttribute('r', '1');
				this._cursor.textEl.textContent = '';
				this._cursor.textBackEl.textContent = '';
				break;
			case 'move':
				cssClass = 'canvas_tool_' + (cursor.isDown ? 'moving' : 'move');
				break;
			case 'translate':
				cssClass = 'canvas_tool_move-object';
				break;
			case 'rect':
			case 'lasso':
			case 'poly_lasso':
				if (!this._layers.current.isOperable) {
					cssClass = 'canvas_tool_not-operable';
				} else if (this._sel.isPointerOverSelection) {
					cssClass = 'canvas_tool_move-object';
				} else {
					cssClass = 'canvas_tool_arrow';
				}
				break;
			case 'fill':
				if (!this._layers.current.isOperable) {
					cssClass = 'canvas_tool_not-operable';
				} else {
					cssClass = 'canvas_tool_arrow';
				}
				break;
			case 'ellipse':
				if (!this._layers.current.isOperable) {
					cssClass = 'canvas_tool_not-operable';
				} else {
					cssClass = 'canvas_tool_crosshair';
				}

				if (this._currentDrawTool.angle !== 0) {
					this._cursor.textEl.textContent = `${this._currentDrawTool.angle}°`;
					this._cursor.textBackEl.textContent = `${this._currentDrawTool.angle}°`;
				} else {
					this._cursor.textEl.textContent = '';
					this._cursor.textBackEl.textContent = '';
				}
				break;
			case 'zoom':
				cssClass = 'canvas_tool_' + (cursor.isAlt ? 'zoom-out' : 'zoom-in');
				break;
		}

		if (cursor.cssClass !== cssClass) {
			if (cursor.cssClass !== '') {
				classList.remove(cursor.cssClass);
			}
			cursor.cssClass = cssClass;
			classList.add(cssClass);
		}

		if (this._layers.current.isOperable) {
			if ((this._cursor.isShift && (this._tool === 'brush_bg' || this._tool === 'brush' || this._tool === 'erase'))
				|| (this._tool === 'poly_lasso' && this._sel.isDrawingMode)) {
				this._cursor.isTraceNextPoint = true;
				this._renderDisplayCanvas();
			} else if (this._cursor.isTraceNextPoint) {
				this._cursor.isTraceNextPoint = false;
				this._renderDisplayCanvas();
			}
		}
		return this;
	}

	/**
	 * Selects all canvas
	 *
	 * @returns {Canvas}
	 */
	selectAll() {
		this._sel.setPath(this._clipArea.getBorderPath(2));
		this.setTool('rect');
		return this;
	}

	/**
	 * Drops current selection
	 *
	 * @returns {Canvas}
	 */
	dropSelection() {
		this._sel.dropSelection();
		this._saveState('drop-selection');
		this.updateCursor();
		this._renderDisplayCanvas();
		return this;
	}

	/**
	 * Erases the selected area
	 *
	 * @returns {Canvas}
	 */
	async eraseSelectedArea() {
		if (this._sel.hasSelection && this._timeline.fromFrame < this._timeline.toFrame) {
			await this.transformLayer(new CanvasEraser());
		} else if (this._sel.clear()) {
			this.saveCurrentLayerImage();
			this._saveState('clear-selected-area');
		}

		this.dropSelection();
		return this;
	}

	/**
	 * Copies whole image of the current frame
	 *
	 * @returns {Canvas}
	 */
	copyFrames() {
		this._clipboardBuffer = this._layers.current
			.getFramesImageData(this._timeline.fromFrame, this._timeline.toFrame);
		return this;
	}

	/**
	 * Copies selected fragment
	 *
	 * @param {boolean} [erase]
	 * @returns {Canvas}
	 */
	copySelectedFragment(erase = false) {
		if (!this._sel.copyFragment()) {
			return this;
		}

		if (erase) {
			this._sel.clear();
			this._sel.dropSelection();
			this.updateCursor();
			this.saveCurrentLayerImage();
			this._saveState('clear-selected-area');
		}

		this._clipboardBuffer = null;
		this._renderDisplayCanvas();
		return this;
	}

	/**
	 * Pastes fragment from buffer into the center of current selection or viewport
	 *
	 * @param {boolean} [toOriginalPos]
	 * @returns {Canvas}
	 */
	paste(toOriginalPos = false) {
		if (this._clipboardBuffer !== null) {
			this._layers.current.setFramesImageData(this._timeline.current, this._clipboardBuffer);
			this.setFramesSelection(
				this._timeline.current,
				this._timeline.current + this._clipboardBuffer.length - 1
			);

			if (this._timeline.isShadowFrame) {
				this._updateShadowFrame();
			}

			this._saveState('paste-frames');
			return this;
		}

		const scale = this._scale;
		this._sel.pasteFragment(
			scale.srcX + scale.srcWidth / 2,
			scale.srcY + scale.srcHeight / 2,
			toOriginalPos
		);
		return this;
	}

	/**
	 * Start drawing new selection or moving already drawn selection area
	 *
	 * @returns {void}
	 * @private
	 */
	_initNewSelection(x, y) {
		if (!this._layers.current.isOperable) {
			return;
		}

		let type = 'free';
		if (this._tool === 'poly_lasso') {
			type = 'poly';
		} else if (this._tool === 'rect') {
			type = 'rect';
		}
		this._sel.processPointerDown(this._convertSourceXY(x, y), this._cursor.isCtrl, type);
	}

	/**
	 * Draws selection or moves the selected area
	 *
	 * @param {number} x
	 * @param {number} y
	 * @param {number} prevX
	 * @param {number} prevY
	 * @param {number} startX
	 * @param {number} startY
	 * @returns {void}
	 * @private
	 */
	_saveSelectionPoint(x, y, prevX, prevY, startX, startY) {
		let scaleFactor = this._scale.factor;
		this._sel.processPointerMove(
			this._convertSourceXY(x, y),
			Math.round((x - startX) / scaleFactor),
			Math.round((y - startY) / scaleFactor),
			this._cursor.isCtrl
		);
		this._renderDisplayCanvas();
		this._events.trigger(EVENT_DRAWING);
	}

	/**
	 * Processes just added selection
	 *
	 * @param {number} x
	 * @param {number} y
	 * @returns {void}
	 * @private
	 */
	_processNewSelection(x, y) {
		const pos = this._convertSourceXY(x, y - this._cursor.offsetTop);
		const isPoly = this._sel.isPolygonalDrawMode;

		if (this._sel.isMovingMode) {
			if (this._sel.hasFragment) {
				// selection has extracted fragment image
				this.saveCurrentLayerImage();
			}
			this._saveState('move-selection');
		}

		if (this._sel.processPointerUp(pos)) {
			this._saveState('draw-selection');
			this._renderDisplayCanvas();
		}

		if (isPoly) {
			this._draw.updateLastXY(pos);
			this._renderDisplayCanvas();
		}
	}

	/**
	 * Initializes tool for moving viewport
	 *
	 * @returns {void}
	 * @private
	 */
	_initViewportMoving() {
		this._scale.prevCenterX = this._scale.centerX;
		this._scale.prevCenterY = this._scale.centerY;
	}

	/**
	 * Moves viewport
	 *
	 * @param {number} x
	 * @param {number} y
	 * @param {number} prevX
	 * @param {number} prevY
	 * @param {number} startX
	 * @param {number} startY
	 * @returns {void}
	 * @private
	 */
	_moveViewport(x, y, prevX, prevY, startX, startY) {
		let dx = x - startX,
			dy = y - startY,
			scale = this._scale;
		scale.centerX = scale.prevCenterX - dx / scale.factor;
		scale.centerY = scale.prevCenterY - dy / scale.factor;
		this._calcScale();
		this._renderDisplayCanvas();
	}

	/**
	 * Handles mouse pointer down event
	 *
	 * @param {MouseEvent} e
	 * @returns {void}
	 * @private
	 */
	_onMouseDown(e) {
		if (e.button === 2) {
			e.preventDefault();
			e.stopPropagation();
			return;
		}

		if (!this._cursor.isDown) {
			this._cursor.type = 'mouse';
			this._precessPointerDown(e.clientX, e.clientY);
		}
	}

	/**
	 * @param {MouseEvent} e
	 * @returns {void}
	 * @private
	 */
	_onContextMenu(e) {
		e.preventDefault();
		new CanvasContextMenu(this, e.clientX, e.clientY);
	}

	/**
	 * Processes pointer down event
	 *
	 * @param {number} x
	 * @param {number} y
	 * @returns {void}
	 * @private
	 */
	_precessPointerDown(x, y) {
		let bBox = this._displayCanvas.el.getBoundingClientRect();
		let cursor = this._cursor;
		cursor.elX = bBox.left;
		cursor.elY = bBox.top;
		cursor.lastClientX = x;
		cursor.lastClientY = y;
		cursor.x = x - cursor.elX;
		cursor.y = y - cursor.elY;
		cursor.startX = cursor.x;
		cursor.startY = cursor.y;
		cursor.isDown = true;

		if (this._cursor.onMove !== null) {
			if (cursor.type === 'mouse') {
				document.addEventListener('mousemove', this._handlers.mouseMove, true);
			} else {
				document.addEventListener('touchmove', this._handlers.touchMove, {passive: false});
			}
		}

		if (cursor.type === 'mouse') {
			document.addEventListener('mouseup', this._handlers.mouseUp, true);
		} else {
			document.addEventListener('touchend', this._handlers.touchUp, false);
		}

		if (cursor.onDown !== null) {
			cursor.onDown.call(this, cursor.x, cursor.y);
		}
		this.updateCursor();
	}

	/**
	 * Handles mouse pointer move event
	 *
	 * @param {MouseEvent} e
	 * @returns {void}
	 * @private
	 */
	_onMouseMove(e) {
		if (this._cursor.isDown) {
			if (this._cursor.type === 'mouse') {
				this._processPointerMove(e.clientX, e.clientY);
			}
		}
	}

	/**
	 * Handles mouse pointer move event
	 *
	 * @param {MouseEvent} e
	 * @returns {void}
	 * @private
	 */
	_onMouseMoveShadow(e) {
		this._setCursorPosition(e.clientX, e.clientY, e.ctrlKey || e.metaKey);
	}

	/**
	 * Updates cursor position and view
	 *
	 * @param {number} clientX
	 * @param {number} clientY
	 * @param {boolean} isCtrl
	 * @private
	 */
	_setCursorPosition(clientX, clientY, isCtrl) {
		let {x, y} = this._convertClientXY(clientX, clientY);
		this._cursor.x = x;
		this._cursor.y = y;
		this._cursor.el.style.transform = `translate3d(${x}px, ${y + this._cursor.offsetTop}px, 0)`;

		let srcXY = this._convertSourceXY(clientX, clientY - this._cursor.offsetTop);
		this._cursor.srcX = srcXY.x;
		this._cursor.srcY = srcXY.y;

		if (!this._cursor.isDown) {
			if (this._cursor.isTraceNextPoint) {
				this._renderDisplayCanvas();
			}
			if (this._tool === 'rect'
				|| this._tool === 'lasso'
				|| this._tool === 'poly_lasso'
				|| this._tool === 'fill') {
				if (this._sel.hasSelection) {
					// check if cursor is over the selected area
					let isOver = this._sel.testPoint(this._cursor.x * this._dprFactor, this._cursor.y * this._dprFactor);
					if (this._sel.isPointerOverSelection !== isOver) {
						this._sel.setPointerOverSelectionFlag(isOver);
						this.updateCursor();
					}
				} else {
					if (this._sel.isPointerOverSelection) {
						this._sel.setPointerOverSelectionFlag(false);
						this.updateCursor();
					}
				}
			}
			if (this._cursor.isCtrl !== isCtrl) {
				this._cursor.isCtrl = isCtrl;
				this.updateCursor();
			}
		}
	}

	/**
	 * Handles touch pointer (finger, stylus, pen ...) start event
	 *
	 * @param {TouchEvent|Event} e
	 * @returns {void}
	 * @private
	 */
	_onTouchDown(e) {
		e.preventDefault();
		if (!this._cursor.isDown && e.touches && e.touches.length > 0) {
			e = e.touches[0];
			this._cursor.type = 'touch';
			this._precessPointerDown(e.clientX, e.clientY);
		}
	}

	/**
	 * Handles touch pointer (finger, stylus, pen ...) move event
	 *
	 * @param {TouchEvent} e
	 * @returns {void}
	 * @private
	 */
	_onTouchMove(e) {
		e.preventDefault();
		if (this._cursor.type === 'touch' && e.touches && e.touches.length > 0) {
			let isCtrl = e.ctrlKey || e.metaKey;
			e = e.touches[0];
			this._setCursorPosition(e.clientX, e.clientY, isCtrl);
			this._processPointerMove(e.clientX, e.clientY);
		}
	}

	/**
	 * Handles touch pointer (finger, stylus, pen ...) up event
	 *
	 * @param {TouchEvent} e
	 * @returns {void}
	 * @private
	 */
	_onTouchUp(e) {
		e.preventDefault();
		if (this._cursor.type === 'touch') {
			this._processPointerUp(
				this._cursor.lastClientX,
				this._cursor.lastClientY,
				e.ctrlKey || e.metaKey,
				e.shiftKey
			);
		}
	}

	/**
	 * Processes pointer move event
	 *
	 * @param {number} x
	 * @param {number} y
	 * @returns {void}
	 * @private
	 */
	_processPointerMove(x, y) {
		let cursor = this._cursor,
			newX = x - cursor.elX,
			newY = y - cursor.elY;

		if (cursor.onMove !== null) {
			cursor.onMove.call(this, newX, newY, cursor.x, cursor.y, cursor.startX, cursor.startY);
		}

		cursor.lastClientX = x;
		cursor.lastClientY = y;
		cursor.x = newX;
		cursor.y = newY;
	}

	/**
	 * Handles mouse pointer up event
	 *
	 * @param {MouseEvent} e
	 * @returns {void}
	 * @private
	 */
	_onMouseUp(e) {
		if (this._cursor.type === 'mouse') {
			this._processPointerUp(e.clientX, e.clientY, e.ctrlKey || e.metaKey, e.shiftKey);
		}
	}

	/**
	 * Processes pointer up event
	 *
	 * @private
	 * @param {number} clientX
	 * @param {number} clientY
	 * @param {boolean} ctrlKey
	 * @param {boolean} shiftKey
	 * @returns {void}
	 */
	_processPointerUp(clientX, clientY, ctrlKey, shiftKey) {
		let cursor = this._cursor;
		if (this._cursor.type === 'mouse') {
			document.removeEventListener('mousemove', this._handlers.mouseMove, true);
			document.removeEventListener('mouseup', this._handlers.mouseUp, true);
		} else {
			document.removeEventListener('touchmove', this._handlers.touchMove, false);
			document.removeEventListener('touchend', this._handlers.touchUp, false);
		}
		if (cursor.onUp) {
			cursor.onUp.call(this, clientX, clientY);
		}
		this.updateCursor();
		cursor.isDown = false;
		cursor.isCtrl = ctrlKey;
		let prevShiftFlag = cursor.isShift;
		cursor.isShift = shiftKey;

		if (prevShiftFlag !== cursor.isShift) {
			// cancel draw highlight of the next line path
			this._cursor.isTraceNextPoint = false;
			this._renderDisplayCanvas();
		}
	}

	/**
	 * Handles mouse wheel event
	 *
	 * @param {MouseEvent} e
	 * @returns {void}
	 * @private
	 */
	_onMouseWheel(e) {
		let v = 0;
		e.preventDefault();
		if (e.type === 'mousewheel') {
			v = e.wheelDelta;
		} else if (e.type === 'MozMousePixelScroll') {
			v = -e.detail;
		}
		if (v !== 0) {
			this.zoomPointByXY(this._convertClientXY(e.clientX, e.clientY), v > 0);
		}
	}

	/**
	 * Renders work canvas into display canvas
	 *
	 * @private
	 * @returns {void}
	 */
	_renderDisplayCanvas() {
		let render = this._render;
		if (render.ignore) {
			return;
		}
		if (!render.flag) {
			render.requested = true;
			return;
		}

		const requestAnimationFrame = render.requestAnimationFrame;
		if (!render.requested) {
			requestAnimationFrame(this._handlers.renderDisplayCanvas);
			return;
		}

		const displayCanvas = this._displayCanvas.el;
		const width = displayCanvas.width / this._dprFactor;
		const height = displayCanvas.height / this._dprFactor;
		const workCanvas = this._workCanvas.el;
		const ctx = this._displayCanvas.ctx;
		const scale = this._scale;

		ctx.clearRect(0, 0, displayCanvas.width, displayCanvas.height);

		let dx = 0;
		let dy = 0;
		let x = Math.round(scale.srcX);
		let y = Math.round(scale.srcY);
		let w = Math.round(scale.srcWidth);
		let h = Math.round(scale.srcHeight);

		// prevent anti aliasing when scale
		ctx.mozImageSmoothingEnabled
			= ctx.webkitImageSmoothingEnabled
			= ctx.msImageSmoothingEnabled
			= ctx.imageSmoothingEnabled
			= this._scale.factor === 0.25;

		if (this._displayMode === DISPLAY_MODE_VIEW) {
			this._neighborScenes.drawScenes(
				1, ctx, this._timeline.current, this._sceneWidth, this._sceneHeight, x, y,
				scale.factor * this._dprFactor
			);
		}

		ctx.drawImage(
			workCanvas, 
			x,  // source x
			y,  // source y
			w,  // source width
			h,  // source height
			dx * this._dprFactor, 
			dy * this._dprFactor, 
			width * this._dprFactor, 
			height * this._dprFactor
		);

		if (this._displayMode === DISPLAY_MODE_VIEW) {
			this._neighborScenes.drawScenes(
				2, ctx, this._timeline.current, this._sceneWidth, this._sceneHeight, x, y,
				scale.factor * this._dprFactor
			);
			this._clipArea.drawBorders(ctx, x, y, scale.factor * this._dprFactor);
		}

		if (render.grid) {
			ctx.save();
			this._clipArea.drawGrid(ctx, x, y, scale.factor * this._dprFactor, SCENE_GRID_INDENT);
			ctx.restore();
		}

		this._sel.render(ctx, x, y, scale.factor * this._dprFactor);

		if (this._cursor.isTraceNextPoint) {
			let cur = this._cursor;
			this._draw.drawTraceFromLastXY(ctx, cur.srcX, cur.srcY, x, y, scale.factor * this._dprFactor);
		}

		this._render.requested = false;

		requestAnimationFrame(this._handlers.renderDisplayCanvas);
	}

	/**
	 * Clears canvas for temporary draws
	 *
	 * @private
	 * @param {boolean} [includeBuffer]
	 * @returns {void}
	 */
	_clearTempCanvas(includeBuffer = false) {
		let el = this._tempCanvas.el;
		this._tempCanvas.ctx.clearRect(0, 0, el.width, el.height);
		if (includeBuffer) {
			this._tempBufferCanvas.ctx.clearRect(0, 0, el.width, el.height);
		}
	}

	/**
	 * Merges temp canvas into the layer canvas
	 *
	 * @private
	 * @returns {void}
	 */
	_mergeTemp2LayerCanvas() {
		let el = this._tempCanvas.el, ctx;

		if (this._currentDrawTool.tool === 'erase') {
			// make clone of the temp canvas because of strange issue: second call of drawImage
			// does not work for the same canvas object
			const newCanvas = document.createElement('canvas');
			newCanvas.width = el.width;
			newCanvas.height = el.height;
			newCanvas.getContext('2d').drawImage(el, 0, 0);

			ctx = this._layerCanvas.ctx;
			ctx.save();
			ctx.globalCompositeOperation = 'destination-out';
			ctx.globalAlpha = this._currentDrawTool.fill.alphaNormal;
			ctx.globalAlpha = this._currentDrawTool.fill.alphaNormal;
			ctx.drawImage(el, 0, 0, el.width, el.height, 0, 0, el.width, el.height);
			ctx.restore();
		} else {
			ctx = this._layerCanvas.ctx;
			ctx.save();
			this._clipArea.createBorderPath(ctx);
			ctx.clip();

			if (this._currentDrawTool.tool === 'brush_bg') {
				ctx.globalCompositeOperation = 'destination-over';
			} else {
				ctx.globalCompositeOperation = 'source-over';
			}

			ctx.globalAlpha = this._currentDrawTool.fill.alphaNormal;
			ctx.drawImage(el, 0, 0, el.width, el.height, 0, 0, el.width, el.height);
			ctx.restore();
		}
		this._clearTempCanvas(true);
	}

	/**
	 * Draws temp canvas to work canvas
	 *
	 * @param {T_Rectangle} rect
	 * @private
	 * @returns {void}
	 */
	_drawTemp2WorkCanvas(rect) {
		let ctx = this._workCanvas.ctx,
			x = rect.left,
			y = rect.top,
			w = rect.right - rect.left,
			h = rect.bottom - rect.top,
			tl = this._timeline;

		x = Math.floor(x);
		y = Math.floor(y);
		w = Math.ceil(w);
		h = Math.ceil(h);

		if (w <= 0 || h <= 0) {
			return;
		}

		ctx.clearRect(x, y, w, h);

		ctx.save();
		this._clipArea.createBorderPath(ctx);
		ctx.clip();

		// before we need to draw current layer
		// check if layer is visible
		if (this._layers.current.isVisible()) {
			ctx.globalAlpha = this._layers.current.getOpacity() / 100;
			ctx.globalCompositeOperation = CanvasBlendingModeToCompositeOperation[this._layers.current.getBlendingMode()];
			ctx.drawImage(this._layerCanvas.el, x, y, w, h, x, y, w, h);
			ctx.globalAlpha = 1;
			ctx.globalCompositeOperation = 'source-over';
		}

		// apply new draw OR ERASE (!!!), that is why we start drawing from current layer and ignore all layers under
		if (this._currentDrawTool.tool === 'erase') {
			ctx.globalCompositeOperation = 'destination-out';
		} else if (this._currentDrawTool.tool === 'brush_bg') {
			// draw under the current image
			ctx.globalCompositeOperation = 'destination-over';
		} else {
			// normal draw
			ctx.globalCompositeOperation = 'source-over';
		}

		// apply current color alpha
		ctx.globalAlpha = this._currentDrawTool.fill.alphaNormal;
		// merge two temp canvases, with new and old draw
		this._tempCanvas.ctx.drawImage(this._tempBufferCanvas.el, 0, 0);
		ctx.drawImage(this._tempCanvas.el, x, y, w, h, x, y, w, h);

		// restore alpha
		ctx.globalAlpha = 1;

		// now we draw all layers under current one
		// for that we need to change composite method
		ctx.globalCompositeOperation = 'destination-over';

		// if shadow frame enabled we draw previous frame with opacity
		// this helps to draw animation
		if (tl.isShadowFrame && tl.shadowImg !== null && this._layers.current.isOperable) {
			// draw previous frame as shadow
			ctx.globalAlpha = 0.3;
			ctx.drawImage(tl.shadowImg, x, y, w, h, x, y, w, h);
			ctx.globalAlpha = 1;
		}

		// at first we draw all layers that under current one
		ctx.drawImage(this._layersUnder.el, x, y, w, h, x, y, w, h);

		if (this._displayMode === DISPLAY_MODE_EDIT) {
			ctx.fillStyle = 'white';
			ctx.fillRect(x, y, w, h);
		}

		// restore composite method and draw all layers above the current one
		ctx.globalCompositeOperation = 'source-over';

		// draw all layers that over the current one
		this._drawLayersAbove(x, y, w, h);

		ctx.restore();
	}

	/**
	 * @private
	 */
	_drawLayersAbove(x, y, w, h) {
		for (const renderItem of this._layersAboveRenderList) {
			if (renderItem instanceof CanvasLayer) {
				renderItem.drawFrame(this._workCanvas, null, true, x, y, w, h);
			} else {
				this._workCanvas.ctx.drawImage(renderItem.el, x, y, w, h, x, y, w, h);
			}
		}
	}

	/**
	 * Updates shadow frame
	 *
	 * @private
	 * @returns {void}
	 */
	_updateShadowFrame() {
		if (!this._layers.current.isOperable) {
			return;
		}

		let tl = this._timeline,
			frm = this._layers.current.getPreviousKeyframeIndex();

		if (!tl.isShadowFrame || frm === null) {
			if (tl.shadowImg) {
				tl.shadowFrame = null;
				tl.shadowImg = null;
				this._updateWorkCanvas();
				this._renderDisplayCanvas();
				this.events.trigger(EVENT_UPDATE_TIMELINE);
			}
			return;
		}

		tl.shadowFrame = frm;
		tl.shadowImg = null;
		this._layers.current.getImagePromise(frm).then(() => {
			if (tl.shadowFrame === frm && tl.isShadowFrame) {
				let img = this._layers.current.getImage(frm);
				if (img) {
					tl.shadowImg = img;
				}
				this._updateWorkCanvas();
				this._renderDisplayCanvas();
				this.events.trigger(EVENT_UPDATE_TIMELINE);
			}
		});
	}

	/**
	 * Updates canvas which contains full picture for rendering on display canvas
	 *
	 * @param {number} x
	 * @param {number} y
	 * @param {?number} [w]
	 * @param {?number} [h]
	 * @returns {void}
	 * @private
	 */
	_updateWorkCanvas(x = 0, y = 0, w = null, h = null) {
		if (w === null) {
			w = this._sceneWidth - x;
		}
		if (h === null) {
			h = this._sceneHeight - y;
		}
		if (w <= 0 || h <= 0) {
			return;
		}
		let ctx = this._workCanvas.ctx, tl = this._timeline;
		x = Math.floor(x);
		y = Math.floor(y);
		w = Math.ceil(w);
		h = Math.ceil(h);

		ctx.clearRect(x, y, w, h);
		ctx.save();
		this._clipArea.createBorderPath(ctx);
		ctx.clip();

		if (this._displayMode === DISPLAY_MODE_EDIT) {
			ctx.fillStyle = 'white';
			ctx.fillRect(x, y, w, h);
		}

		ctx.drawImage(this._layersUnder.el, x, y, w, h, x, y, w, h);

		if (tl.isShadowFrame && tl.shadowImg !== null) {
			// draw previous frame as shadow
			ctx.globalAlpha = 0.3;
			ctx.drawImage(tl.shadowImg, x, y, w, h, x, y, w, h);
			ctx.globalAlpha = 1;
		}

		if (this._translate !== null) {
			ctx.save();
			this._translate.applyForContext(ctx);
		}

		// check if current layer is visible
		if (this._layers.current.isVisible()) {
			// draw current layer
			ctx.globalAlpha = this._layers.current.getOpacity() / 100;
			ctx.globalCompositeOperation = CanvasBlendingModeToCompositeOperation[this._layers.current.getBlendingMode()];
			ctx.drawImage(this._layerCanvas.el, x, y, w, h, x, y, w, h);
			ctx.globalAlpha = 1;
			ctx.globalCompositeOperation = 'source-over';
		}

		if (this._translate !== null) {
			ctx.restore();
		}

		// draw all layers that over the current one
		this._drawLayersAbove(x, y, w, h);

		ctx.restore();
	}

	/**
	 * Gets scene properties
	 *
	 * @returns {T_CanvasProps}
	 */
	getProps() {
		return Object.assign({}, this._canvasProps);
	}

	/**
	 * Sets scene properties
	 *
	 * @param {T_CanvasProps} data
	 * @returns {Canvas}
	 */
	updateProps(data) {
		for (let [key, value] of Object.entries(data)) {
			if (this._canvasProps.hasOwnProperty(key)) {
				this._canvasProps[key] = value;
			}
		}
		return this;
	}

	/**
	 * Returns TRUE if the current canvas state saved to the server
	 *
	 * @returns {boolean}
	 */
	isStateSaved() {
		// count number of weighty changes
		const excludeList = ['change-frame', 'init', 'drop-selection', 'draw-selection'];
		let weightyChanges = 0;
		for (let i = 0; i < this._history.records.length && i < this._history.index; i++) {
			if (!excludeList.includes(this._history.records[i].type)) {
				weightyChanges++;
			}
		}

		if (weightyChanges === 0) {
			return true;
		}

		return this._history.records[this._history.index - 1].isSaved;
	}

	/**
	 * Marks current state of canvas as saved to the server
	 *
	 * @returns {Canvas}
	 */
	markStateAsSaved() {
		if (this._history.index > 0) {
			this._history.records[this._history.index - 1].isSaved = true;
		}
		this.events.trigger(EVENT_SAVED);
		return this;
	}

	/**
	 * Returns canvas data
	 *
	 * @param {boolean} [onlyDelta=true] Include to data only changed sources
	 * @returns {T_CanvasData}
	 */
	async getData(onlyDelta = true) {
		let layers = [];
		for (let layer of this._layers.list) {
			layers.push(await layer.getData(false, onlyDelta));
		}
		let data = Object.assign({}, this._canvasProps);
		data.layers = layers;
		data.neighbors = [];
		return data;
	}

	/**
	 * Sets canvas data
	 *
	 * @param {T_CanvasData} data
	 * @returns {void}
	 */
	async setData(data) {
		this.updateProps(data);
		this._dataSet.clear();
		this._trash.layers.clear();
		this._layers.list = [];
		this._layers.current = null;

		for (let layerData of data.layers) {
			this._createLayer({data: layerData}, false);
		}

		await this._neighborScenes.setData(data.neighbors);
		this._history.index = 0;
		this._history.records = [];
		this._saveState('init');
		this._events.trigger(EVENT_UPDATE_TIMELINE);
	}

	/**
	 * Makes garbage collection (gc): deleted unused data
	 *
	 * @returns {Canvas}
	 */
	collectGarbage() {
		let usedLayers = new Set(),
			fillMap = new Set();

		for (let state of this._history.records) {
			for (let l of state.layers) {
				usedLayers.add(l.id);
				for (let f of l.frames) {
					if (f.key !== '') {
						fillMap.add(f.key);
					}
				}
			}
			if (state.selection) {
				if (state.selection.fragmentData) {
					fillMap.add(state.selection.fragmentData.key);
				}
			}
		}
		// clear trash
		[...this._trash.layers.keys()]
			.filter(id => !usedLayers.has(id))
			.map(id => this._trash.layers.delete(id));

		// collect used data set items from all layers
		for (let l of this._trash.layers.values()) {
			l.fillDataSetKeys(fillMap);
		}
		for (let l of this._layers.list) {
			l.fillDataSetKeys(fillMap);
		}

		this._sel.fillDataSetKeys(fillMap);

		if (fillMap.size > 0) {
			this._dataSet.deleteAllExcept([...fillMap]);
		}
		return this;
	}
}
